/**
 * Data Collection API Layer
 *
 * Handles submission of ALL data collection entries to the backend.
 * Every form type uses a single POST to /api/v1/data-collection
 * with form-specific data stored in the raw_data JSONB field.
 * The datasheet_type_id identifies the form type.
 */

import axiosServices from 'utils/axios';

const API = '/api/v1';

/**
 * All form types route through data-collection with raw_data JSONB
 */
const FORM_TYPE_CONFIG = {
  'cold-probe': { entryType: 'data-collection' },
  'multi-trial': { entryType: 'data-collection' },
  'skill-tracking': { entryType: 'data-collection' },
  'behavior-tracking': { entryType: 'data-collection' },
  'behavior-interval': { entryType: 'data-collection' },
  abc: { entryType: 'data-collection' },
  'daily-mand': { entryType: 'data-collection' },
  'maintenance-probe': { entryType: 'data-collection' },
  'toileting-program': { entryType: 'data-collection' },
  'parent-training': { entryType: 'data-collection' },
  'injury-report': { entryType: 'data-collection' },
  'lesson-plan': { entryType: 'data-collection' },
  'session-notes': { entryType: 'data-collection' },
  'session-plan': { entryType: 'data-collection' },
  'ot-data-sheet': { entryType: 'data-collection' },
  'slp-data-sheet': { entryType: 'data-collection' }
};

/**
 * Submit a data collection entry to the backend
 *
 * @param {string} formType - The type of form (e.g., 'cold-probe', 'injury-report')
 * @param {Object} data - The form data to submit
 * @param {Object} metadata - Additional metadata (sessionId, childId, etc.)
 * @returns {Promise<Object>} - Response with success status and server ID
 */
export const submitDataEntry = async (formType, data, metadata = {}) => {
  const config = FORM_TYPE_CONFIG[formType];
  if (!config) {
    throw new Error(`Unknown form type: ${formType}`);
  }

  const sessionId = metadata.sessionId || data.sessionId || data.session_id;

  if (!sessionId) {
    console.warn(`[API] sessionId missing for ${formType} entry - skipping submission`);
    throw new Error('sessionId is required for data collection submission');
  }

  try {
    const payload = buildPayload(formType, data, metadata);

    // If payload is null, session_id was not a valid UUID — throw so syncManager
    // keeps the entry in IndexedDB for retry instead of silently deleting it.
    if (!payload) {
      throw new Error(`Invalid session UUID for ${formType} — cannot sync to server`);
    }

    const response = await axiosServices.post(`${API}/data-collection`, payload);

    return {
      success: true,
      id: response.data.id,
      serverTimestamp: response.data.created_at || new Date().toISOString(),
      message: `${formType} entry saved successfully`
    };
  } catch (error) {
    console.error(`[API] Failed to submit ${formType} entry:`, error);
    console.error(`[API] Error details:`, JSON.stringify(error, Object.getOwnPropertyNames(error)));
    throw error;
  }
};

/**
 * Extract total_trials, correct_trials, percentage from form-specific data structures.
 * Each form type stores its metrics differently.
 */
const extractTrialMetrics = (formType, data) => {
  let totalTrials = null;
  let correctTrials = null;
  let percentage = null;

  switch (formType) {
    case 'cold-probe':
    case 'maintenance-probe':
    case 'skill-tracking': {
      const targets = data.targets || [];
      totalTrials = data.totalTargets || targets.length || null;
      correctTrials = targets.filter((t) => t.response === '+' || t.response === 'correct').length;
      if (totalTrials > 0) {
        percentage = Math.round((correctTrials / totalTrials) * 100);
      }
      break;
    }
    case 'multi-trial': {
      totalTrials = data.totalCompleted || null;
      correctTrials = data.totalCorrect || null;
      percentage = data.accuracy != null ? Math.round(data.accuracy) : null;
      break;
    }
    case 'behavior-tracking': {
      totalTrials = data.totalOccurrences || null;
      break;
    }
    case 'behavior-interval': {
      // Support both flat format (top-level fields) and nested format (currentRecording)
      const rec = data.currentRecording || data;
      totalTrials = rec.totalIntervals || rec.totalRecorded || null;
      // Use the relevant percent based on interval type
      const intervalPct = rec.intervalType === 'partial' ? rec.partialPercent : rec.wholePercent;
      percentage = intervalPct != null ? Math.round(intervalPct) : null;
      break;
    }
    case 'daily-mand': {
      totalTrials = data.totalMands || null;
      correctTrials = data.independentMands || null;
      if (totalTrials > 0 && correctTrials != null) {
        percentage = Math.round((correctTrials / totalTrials) * 100);
      }
      break;
    }
    case 'toileting-program': {
      const tRecords = data.records || [];
      const tVisits = data.visits || [];

      if (data.dataSheetType === 'type2' || tVisits.length > 0) {
        // Type 2 - Progress Sheet: count visits, success = toilet result '✓'
        totalTrials = tVisits.length || null;
        correctTrials = tVisits.filter((v) => v.toiletResult === '✓').length;
      } else if (tRecords.length > 0) {
        // Type 1 - Data Entry (full form state): count records, success = unprompted
        totalTrials = tRecords.length;
        correctTrials = tRecords.filter((r) => r.prompt === 'U').length;
      } else if (data.toiletVoid !== undefined || data.pantsStatus !== undefined) {
        // Flattened individual record
        totalTrials = 1;
        correctTrials = data.prompt === 'U' ? 1 : 0;
      }

      if (totalTrials > 0) {
        percentage = Math.round((correctTrials / totalTrials) * 100);
      }
      break;
    }
    default:
      break;
  }

  return { totalTrials, correctTrials, percentage };
};

/**
 * Check if a value is a valid UUID string.
 * Returns the value if valid, null otherwise.
 * This filters out mock integer IDs that would cause 422 errors on the backend.
 */
const toUuidOrNull = (value) => {
  if (!value) return null;
  const s = String(value);
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s) ? s : null;
};

/**
 * Extract target_id from form-specific data structures.
 * Forms store targetId inside nested arrays (e.g., targets[0].targetId),
 * not at the top level.
 */
const extractTargetId = (formType, data) => {
  // Check top-level first
  if (data.targetId || data.target_id) {
    return data.targetId || data.target_id;
  }

  switch (formType) {
    case 'cold-probe':
    case 'multi-trial':
    case 'maintenance-probe':
    case 'skill-tracking': {
      // These forms have targets array with targetId per target
      // Check targetId, target_id, and id fields (API targets use id)
      const targets = data.targets || [];
      const first = targets[0];
      if (!first) return null;
      return first.targetId || first.target_id || first.id || null;
    }
    case 'daily-mand': {
      // selectedTarget is a string name, not an ID — check for targetId in targets array
      const targets = data.targets || [];
      const first = targets[0];
      if (!first) return null;
      return first.targetId || first.target_id || first.id || null;
    }
    default:
      return null;
  }
};

/**
 * Map frontend formType codes to backend ref_datasheet_type codes.
 * The backend will resolve these to UUIDs.
 */
const FORM_TYPE_TO_DATASHEET_CODE = {
  'cold-probe': 'cold_probe',
  'multi-trial': 'multi_trial',
  'daily-mand': 'daily_mand',
  'behavior-tracking': 'behavior_frequency',
  'behavior-interval': 'behavior_interval',
  abc: 'abc',
  'maintenance-probe': 'maintenance_probe',
  'skill-tracking': 'skill_tracking',
  'toileting-program': 'toileting_program',
  'parent-training': 'parent_training',
  'injury-report': 'injury_report',
  'lesson-plan': 'lesson_plan',
  'session-notes': 'session_notes',
  'session-plan': 'session_plan',
  'ot-data-sheet': 'ot_data_sheet',
  'slp-data-sheet': 'slp_data_sheet'
};

/**
 * Build the DataCollection payload with raw_data containing all form-specific data
 */
const buildPayload = (formType, data, metadata) => {
  // Parent-level fields that map to data_collection table columns (excluded from raw_data)
  const parentKeys = new Set([
    'sessionId',
    'session_id',
    'targetId',
    'target_id',
    'datasheetTypeId',
    'datasheet_type_id',
    'collectedAt',
    'collected_at',
    'therapistId',
    'collected_by_id',
    'percentage',
    'totalTrials',
    'total_trials',
    'correctTrials',
    'correct_trials',
    'offlineId',
    'offline_id',
    'isOfflineEntry',
    'is_offline_entry',
    'sessionNotes'
  ]);

  // Build raw_data with formType + all form-specific fields
  const rawData = { formType };
  for (const [key, value] of Object.entries(data)) {
    if (!parentKeys.has(key) && key !== 'notes') {
      rawData[key] = value;
    }
  }

  // Extract trial metrics from form-specific data structures
  const metrics = extractTrialMetrics(formType, data);

  // Extract target_id from form-specific nested structures
  const targetId = extractTargetId(formType, data);

  // Map formType to datasheet_type code for backend resolution
  const datasheetTypeCode = FORM_TYPE_TO_DATASHEET_CODE[formType] || null;

  // Notes: forms use 'sessionNotes' or 'notes' — extract from either
  const notes = data.notes || data.sessionNotes || null;

  // Validate UUID fields — mock data uses integer IDs which cause 422 errors
  const sessionId = toUuidOrNull(metadata.sessionId || data.sessionId || data.session_id);
  const collectedById = toUuidOrNull(metadata.therapistId || data.therapistId || data.collected_by_id);

  if (!sessionId) {
    console.warn(`[API] buildPayload: session_id is not a valid UUID, skipping submission for ${formType}`);
    return null;
  }

  return {
    session_id: sessionId,
    target_id: toUuidOrNull(targetId),
    datasheet_type_code: datasheetTypeCode,
    datasheet_type_id: toUuidOrNull(data.datasheetTypeId || data.datasheet_type_id),
    collected_at: data.collectedAt || data.collected_at || metadata.collectedAt || metadata.sessionDate || new Date().toISOString(),
    collected_by_id: collectedById,
    percentage: data.percentage != null ? data.percentage : metrics.percentage,
    total_trials: data.totalTrials || data.total_trials || metrics.totalTrials,
    correct_trials: data.correctTrials || data.correct_trials || metrics.correctTrials,
    notes,
    offline_id: metadata.offlineId || data.offlineId || data.offline_id || null,
    is_offline_entry: metadata.isOfflineEntry || data.isOfflineEntry || data.is_offline_entry || false,
    raw_data: rawData
  };
};

/**
 * Update an existing data collection entry on the server
 *
 * @param {string} serverId - The server-side data collection UUID
 * @param {string} formType - The type of form
 * @param {Object} data - The form data to update
 * @param {Object} metadata - Additional metadata
 * @returns {Promise<Object>} - Response with success status and server ID
 */
export const updateDataEntryOnServer = async (serverId, formType, data, metadata = {}) => {
  try {
    const payload = buildPayload(formType, data, metadata);

    if (!payload) {
      throw new Error(`Invalid session UUID for ${formType} update — cannot sync to server`);
    }

    const response = await axiosServices.put(`${API}/data-collection/${serverId}`, payload);

    return {
      success: true,
      id: response.data.id,
      serverTimestamp: response.data.updated_at || new Date().toISOString(),
      message: `${formType} entry updated successfully`
    };
  } catch (error) {
    console.error(`[API] Failed to update ${formType} entry ${serverId}:`, error);
    throw error;
  }
};

/**
 * Submit a session status update (start/end/cancel session)
 *
 * @param {string} sessionId - The session ID
 * @param {Object} updateData - Status update data
 * @returns {Promise<Object>} - Response with success status
 */
export const submitSessionUpdate = async (sessionId, updateData) => {
  // Determine the correct endpoint based on update type
  let endpoint;
  let method = 'post';
  const payload = { ...updateData };

  if (updateData.status === 'in-progress' || updateData.updateType === 'start-session') {
    endpoint = `${API}/sessions/${sessionId}/start`;
    // Build proper SessionStartRequest — accept both camelCase (from IndexedDB queue)
    // and snake_case (from direct callers)
    payload.actual_start = updateData.actual_start || updateData.actualStartTime || new Date().toISOString();
  } else if (updateData.status === 'completed' || updateData.updateType === 'end-session') {
    // Backend uses /end not /complete
    endpoint = `${API}/sessions/${sessionId}/end`;
    payload.actual_end = updateData.actual_end || updateData.actualEndTime || new Date().toISOString();
  } else if (updateData.status === 'cancelled' || updateData.updateType === 'cancel-session') {
    endpoint = `${API}/sessions/${sessionId}/cancel`;
    // Include cancellation reason from IndexedDB queue if available
    if (updateData.cancellationReason && !payload.cancellation_reason) {
      payload.cancellation_reason = updateData.cancellationReason;
    }
  } else if (updateData.status === 'no-show' || updateData.updateType === 'no-show') {
    endpoint = `${API}/sessions/${sessionId}/no-show`;
  } else {
    // Generic session update
    endpoint = `${API}/sessions/${sessionId}`;
    method = 'put';
  }

  // Remove camelCase fields that came from IndexedDB queue — backend expects snake_case
  delete payload.actualStartTime;
  delete payload.actualEndTime;
  delete payload.cancellationReason;

  try {
    let response;
    if (method === 'put') {
      response = await axiosServices.put(endpoint, payload);
    } else {
      response = await axiosServices.post(endpoint, payload);
    }

    return {
      success: true,
      sessionId,
      serverTimestamp: response.data.updated_at || new Date().toISOString(),
      message: response.data.message || 'Session updated successfully'
    };
  } catch (error) {
    console.error(`[API] Failed to update session ${sessionId}:`, error);
    throw error;
  }
};

/**
 * Batch submit multiple data entries
 *
 * @param {Array<Object>} entries - Array of entries to submit
 * @returns {Promise<Object>} - Results with success/failure counts
 */
export const batchSubmitEntries = async (entries) => {
  const results = {
    successful: [],
    failed: []
  };

  for (const entry of entries) {
    try {
      const response = await submitDataEntry(entry.formType, entry.data, {
        sessionId: entry.sessionId,
        childId: entry.childId,
        childName: entry.childName,
        therapistId: entry.therapistId,
        therapistName: entry.therapistName,
        sessionDate: entry.sessionDate
      });
      results.successful.push({ entryId: entry.id, serverId: response.id });
    } catch (error) {
      results.failed.push({ entryId: entry.id, error: error.message });
    }
  }

  return results;
};

/**
 * Fetch data collections for a session
 * @param {string} sessionId - Session UUID
 * @returns {Promise<Object>} Paginated list
 */
export const getDataCollections = async (sessionId) => {
  const response = await axiosServices.get(`${API}/data-collection`, {
    params: { session_id: sessionId }
  });
  return response.data;
};

/**
 * Fetch data collections for a specific client (child) with optional filters
 * @param {string} clientId - Client UUID
 * @param {Object} params - Optional filter params (start_date, end_date, datasheet_type_code, page, page_size)
 * @returns {Promise<Object>} Paginated list { items, total, page, page_size, total_pages }
 */
export const getClientDataCollections = async (clientId, params = {}) => {
  const response = await axiosServices.get(`${API}/data-collection/client/${clientId}`, { params });
  return response.data;
};

/**
 * Fetch mand items from the reference catalog
 * @param {Object} params - Optional filter params (category, org_id)
 * @returns {Promise<Array>}
 */
export const getMandItems = async (params = {}) => {
  const response = await axiosServices.get(`${API}/materials/mand-items`, { params });
  return response.data;
};

/**
 * Fetch materials from the catalog
 * @param {Object} params - Optional filter params (category_id, org_id)
 * @returns {Promise<Array>}
 */
export const getMaterials = async (params = {}) => {
  const response = await axiosServices.get(`${API}/materials`, { params });
  return response.data;
};

/**
 * Fetch material categories
 * @returns {Promise<Array>}
 */
export const getMaterialCategories = async () => {
  const response = await axiosServices.get(`${API}/materials/categories`);
  return response.data;
};

/**
 * Fetch all active reference behaviors for behavior tracking
 * @returns {Promise<Array>} Array of { id, code, name, description, behavior_type, measurement_type }
 */
export const getRefBehaviors = async () => {
  const response = await axiosServices.get(`${API}/data-collection/ref-behaviors`);
  return response.data;
};

/**
 * Upload a data-collection file (e.g. injury report photo) to Azure File Share.
 *
 * @param {File} file - The File object to upload
 * @param {string} clientId - The client UUID
 * @param {string} formType - The form type slug (e.g. 'injury-report')
 * @returns {Promise<{filename: string, path: string, url: string}>}
 */
export const uploadDataCollectionFile = async (file, clientId, formType) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('client_id', clientId);
  formData.append('form_type', formType);

  const response = await axiosServices.post(`${API}/data-collection/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });

  return response.data;
};

export default {
  submitDataEntry,
  updateDataEntryOnServer,
  submitSessionUpdate,
  batchSubmitEntries,
  getDataCollections,
  getClientDataCollections,
  getMandItems,
  getMaterials,
  getMaterialCategories,
  uploadDataCollectionFile
};
